import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { ResourceState } from "../../shared/ui/resource-state.js";
import type {
  BotDecisionTreeView,
  DecisionTreeNodeView,
  DecisionTreeRuleView,
} from "./decision-tree-models.js";
import { useBotDecisionTree } from "./queries.js";
import {
  DECISION_TREE_MAX_ZOOM,
  DECISION_TREE_MIN_ZOOM,
  fitDecisionTreeZoom,
  stepDecisionTreeZoom,
} from "./decision-tree-zoom.js";

function Rule({ rule }: { readonly rule: DecisionTreeRuleView }) {
  if (rule.children.length > 0) {
    return (
      <div className="decision-rule-group">
        <span>{rule.condition?.toUpperCase() ?? "GROUP"}</span>
        <ul>
          {rule.children.map((child, index) => (
            <li key={`${child.label}-${index}`}>
              <Rule rule={child} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="decision-rule">
      <strong>{rule.label}</strong>
      <code>
        {[rule.field, rule.operator, rule.value].filter((part) => part !== undefined).join(" ")}
      </code>
    </div>
  );
}

function NodeCard({
  node,
  onSelectNode,
  selected,
}: {
  readonly node: DecisionTreeNodeView;
  readonly onSelectNode?: (nodeId: string) => void;
  readonly selected: boolean;
}) {
  return (
    <article
      className={`decision-node decision-node--${node.type}${selected ? " decision-node--selected" : ""}`}
      {...(onSelectNode === undefined
        ? {}
        : {
            onClick: () => onSelectNode(node.id),
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
              if (event.key === "Enter" || event.key === " ") onSelectNode(node.id);
            },
            role: "button",
            tabIndex: 0,
          })}
    >
      <header>
        <span>{node.type}</span>
        <strong>{node.label}</strong>
      </header>
      {node.rule === undefined ? null : <Rule rule={node.rule} />}
      {node.action === undefined ? null : (
        <dl>
          <div>
            <dt>Action</dt>
            <dd>{node.action.actionName}</dd>
          </div>
          {node.action.orderType === undefined ? null : (
            <div>
              <dt>Order</dt>
              <dd>{node.action.orderType}</dd>
            </div>
          )}
          {node.action.closeFraction === undefined ? null : (
            <div>
              <dt>Close</dt>
              <dd>{Math.round(node.action.closeFraction * 100)}%</dd>
            </div>
          )}
          {node.action.stop === undefined ? null : (
            <div>
              <dt>Stop</dt>
              <dd>{node.action.stop}</dd>
            </div>
          )}
          {node.action.target === undefined ? null : (
            <div>
              <dt>Target</dt>
              <dd>{node.action.target}</dd>
            </div>
          )}
        </dl>
      )}
      <small>{node.id}</small>
    </article>
  );
}

function TreeBranch({
  incomingCondition,
  nodeId,
  path,
  onSelectNode,
  selectedNodeId,
  tree,
}: {
  readonly incomingCondition?: string;
  readonly nodeId: string;
  readonly onSelectNode?: (nodeId: string) => void;
  readonly path: readonly string[];
  readonly selectedNodeId?: string;
  readonly tree: BotDecisionTreeView;
}) {
  const node = tree.nodes.find((candidate) => candidate.id === nodeId);
  if (node === undefined) {
    return (
      <li>
        <div className="decision-node decision-node--missing">Missing node: {nodeId}</div>
      </li>
    );
  }
  if (path.includes(nodeId)) {
    return (
      <li>
        <div className="decision-node decision-node--missing">Cycle to {nodeId}</div>
      </li>
    );
  }
  const children = tree.edges.filter((edge) => edge.source === nodeId);
  const nextPath = [...path, nodeId];
  return (
    <li>
      {incomingCondition === undefined ? null : (
        <span className={`branch-label branch-label--${incomingCondition.toLowerCase()}`}>
          {incomingCondition}
        </span>
      )}
      <NodeCard
        node={node}
        {...(onSelectNode === undefined ? {} : { onSelectNode })}
        selected={selectedNodeId === node.id}
      />
      {children.length === 0 ? null : (
        <ul>
          {children.map((edge) => (
            <TreeBranch
              {...(edge.condition === undefined ? {} : { incomingCondition: edge.condition })}
              key={`${nextPath.join("/")}:${edge.id}`}
              nodeId={edge.target}
              {...(onSelectNode === undefined ? {} : { onSelectNode })}
              path={nextPath}
              {...(selectedNodeId === undefined ? {} : { selectedNodeId })}
              tree={tree}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function DecisionTree({
  onSelectNode,
  selectedNodeId,
  tree,
}: {
  readonly onSelectNode?: (nodeId: string) => void;
  readonly selectedNodeId?: string;
  readonly tree: BotDecisionTreeView;
}) {
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState<string | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const fullscreenButtonRef = useRef<HTMLButtonElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const panRef = useRef<{
    readonly pointerId: number;
    readonly startLeft: number;
    readonly startTop: number;
    readonly startX: number;
    readonly startY: number;
  } | null>(null);
  const root = tree.nodes.find((node) => node.type === "start") ?? tree.nodes[0];
  const fitToView = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (viewport === null || content === null) return;
    setZoom(
      fitDecisionTreeZoom(
        viewport.clientWidth,
        viewport.clientHeight,
        content.scrollWidth,
        content.scrollHeight,
      ),
    );
    viewport.scrollTo({ left: 0, top: 0 });
  }, []);

  useEffect(() => {
    let wasFullscreen = false;
    const syncFullscreen = () => {
      const active = document.fullscreenElement === shellRef.current;
      setIsFullscreen(active);
      if (wasFullscreen && !active) fullscreenButtonRef.current?.focus();
      wasFullscreen = active;
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  useLayoutEffect(() => {
    fitToView();
  }, [fitToView, isFullscreen, tree]);

  const toggleFullscreen = async () => {
    const shell = shellRef.current;
    if (shell === null) return;
    setFullscreenError(null);
    try {
      if (document.fullscreenElement === shell) {
        await document.exitFullscreen();
      } else if (typeof shell.requestFullscreen === "function") {
        await shell.requestFullscreen();
      } else {
        setFullscreenError("Full screen is not supported by this browser.");
      }
    } catch {
      setFullscreenError("Could not change full-screen mode. Please try again.");
    }
  };

  if (root === undefined) {
    return (
      <ResourceState
        kind="degraded"
        title="Decision tree is empty"
        description="The imported snapshot did not contain a readable root node."
      />
    );
  }
  return (
    <div className="decision-tree-shell" ref={shellRef}>
      {fullscreenError === null ? null : (
        <p className="decision-tree-fullscreen-error" role="alert">
          {fullscreenError}
        </p>
      )}
      <div className="decision-tree-controls" role="toolbar" aria-label="Decision tree controls">
        <button
          aria-label="Zoom in"
          disabled={zoom >= DECISION_TREE_MAX_ZOOM}
          onClick={() => setZoom((current) => stepDecisionTreeZoom(current, 1))}
          title="Zoom in"
          type="button"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        <button
          aria-label="Zoom out"
          disabled={zoom <= DECISION_TREE_MIN_ZOOM}
          onClick={() => setZoom((current) => stepDecisionTreeZoom(current, -1))}
          title="Zoom out"
          type="button"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M5 12h14" />
          </svg>
        </button>
        <button aria-label="Fit view" onClick={fitToView} title="Fit view" type="button">
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M8 4H4v4M16 4h4v4M20 16v4h-4M4 16v4h4" />
          </svg>
        </button>
        <button
          aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
          aria-pressed={isFullscreen}
          onClick={() => void toggleFullscreen()}
          ref={fullscreenButtonRef}
          title={isFullscreen ? "Exit full screen (Esc)" : "Enter full screen"}
          type="button"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path
              d={
                isFullscreen
                  ? "M4 9h5V4M9 9 3 3M20 15h-5v5M15 15l6 6"
                  : "M9 3H3v6M3 3l7 7M15 21h6v-6M21 21l-7-7"
              }
            />
          </svg>
        </button>
      </div>
      <div
        className="decision-tree-viewport"
        onPointerCancel={(event) => {
          if (panRef.current?.pointerId === event.pointerId) panRef.current = null;
        }}
        onPointerDown={(event) => {
          if (event.button !== 0 || (event.target as HTMLElement).closest(".decision-node")) return;
          panRef.current = {
            pointerId: event.pointerId,
            startLeft: event.currentTarget.scrollLeft,
            startTop: event.currentTarget.scrollTop,
            startX: event.clientX,
            startY: event.clientY,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
          event.preventDefault();
        }}
        onPointerMove={(event) => {
          const pan = panRef.current;
          if (pan?.pointerId !== event.pointerId) return;
          event.currentTarget.scrollLeft = pan.startLeft - (event.clientX - pan.startX);
          event.currentTarget.scrollTop = pan.startTop - (event.clientY - pan.startY);
        }}
        onPointerUp={(event) => {
          if (panRef.current?.pointerId !== event.pointerId) return;
          panRef.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        ref={viewportRef}
        tabIndex={0}
        aria-label={`Scrollable decision tree at ${Math.round(zoom * 100)}% zoom`}
      >
        <div className="decision-tree-stage" style={{ zoom }}>
          <div className="decision-tree" ref={contentRef}>
            <ul>
              <TreeBranch
                nodeId={root.id}
                {...(onSelectNode === undefined ? {} : { onSelectNode })}
                path={[]}
                {...(selectedNodeId === undefined ? {} : { selectedNodeId })}
                tree={tree}
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BotDecisionTreePanel({ botId }: { readonly botId: string }) {
  const tree = useBotDecisionTree(botId);
  return (
    <div className="content-card section-stack" id="decision-tree">
      <div className="connection-heading">
        <div>
          <p className="eyebrow">Executable active revision</p>
          <h2>Decision tree</h2>
          <p className="section-description">
            {tree.data?.calendar?.kind === "month-end"
              ? "This is the exact executable month-end graph consumed by backtesting. Live execution is not supported."
              : "This is the exact typed graph consumed by backtesting and live execution."}
          </p>
        </div>
        {tree.data === undefined ? null : (
          <span className="status-pill status-pill--ready">Revision {tree.data.revision}</span>
        )}
      </div>
      {tree.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading decision tree"
          description="Reading the active immutable Bot revision."
        />
      ) : tree.error !== null ? (
        <ResourceState
          kind="degraded"
          title="Decision tree unavailable"
          description="The active revision does not contain a typed decision graph."
        />
      ) : tree.data === undefined ? null : (
        <>
          <div className="decision-tree-notice" role="note">
            What you see here is what TradingEngine runs. Changes are saved as a new immutable
            revision.
            {tree.data.calendar?.kind === "month-end"
              ? " Evaluate on the final regular-session minute of each month; fill at the next actual session open. Warm-up uses consecutive completed months. Missing month-end data resets to cash. Backtest only."
              : " The revision can then be backtested or activated."}
          </div>
          <div className="indicator-strip" aria-label="Strategy indicators">
            {tree.data.indicators.map((indicator, index) => (
              <span
                className="indicator-chip"
                key={`${indicator.type}-${indicator.interval}-${index}`}
              >
                <strong>{indicator.type}</strong>
                <span>
                  {indicator.interval}
                  {indicator.parameters.length === 0 ? "" : ` · ${indicator.parameters.join(", ")}`}
                </span>
              </span>
            ))}
          </div>
          <DecisionTree tree={tree.data} />
        </>
      )}
    </div>
  );
}
